import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceStrict } from 'date-fns';
import { ArrowRight, CheckCircle2, MoreVertical, Pencil, Send } from 'lucide-react';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, Select, Skeleton, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAddTaskRemark, useTaskRemarks } from './api';
import { TASK_PRIORITY_LABELS, TASK_PRIORITY_VARIANT, TASK_STATUS_LABELS, TASK_TYPE_LABELS } from './labels';
import { WorkState } from './task-work-state';
import type { Task, TaskRemark, TaskStatus } from './types';

const STATUSES: TaskStatus[] = ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'];

/**
 * One task, and the conversation about getting it done.
 *
 * Three parts, top to bottom: what the task is, what has happened on it, and
 * a box to add the next update. The update box is the point — whoever was
 * given the task writes what they did and what is left, and moves the status
 * in the same breath, so the person who handed it out can read the whole
 * story without chasing anybody.
 *
 * Editing the task itself is a different, rarer act, so it lives behind the
 * three dots rather than competing with the update box for attention.
 */
export function TaskDetailsDialog({
  task: initial,
  nameOf,
  onClose,
  onEdit,
}: {
  task: Task;
  nameOf: (id: string) => string;
  onClose: () => void;
  onEdit: () => void;
}) {
  // The list behind the dialog refetches on its own schedule; the dialog shows
  // the task as the server last returned it to *this* dialog.
  const [task, setTask] = useState(initial);
  useEffect(() => setTask(initial), [initial]);

  const done = task.status === 'DONE' || task.status === 'CANCELLED';
  const overdue = !done && task.dueAt !== null && new Date(task.dueAt) < new Date();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="border-b px-5 py-4 text-left">
          <div className="flex items-start gap-2 pr-6">
            <DialogTitle className={cn('min-w-0 flex-1 leading-snug', done && 'line-through')}>
              {task.title}
            </DialogTitle>
            <Can permission="crm.task.update">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Task actions">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit}>
                    <Pencil className="h-4 w-4" />
                    Edit task
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Can>
          </div>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
              <Badge variant={TASK_PRIORITY_VARIANT[task.priority]} className="text-[10px]">
                {TASK_TYPE_LABELS[task.type]}
              </Badge>
              <Badge variant="secondary" className="text-[10px]">
                {TASK_PRIORITY_LABELS[task.priority]} priority
              </Badge>
              <Badge variant={task.status === 'DONE' ? 'success' : 'secondary'} className="text-[10px]">
                {TASK_STATUS_LABELS[task.status]}
              </Badge>
              {overdue && (
                <Badge variant="destructive" className="text-[10px]">
                  Overdue
                </Badge>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {task.description && (
            <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm">{task.description}</p>
          )}

          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Detail label="Due">
              {task.dueAt ? formatDateTime(task.dueAt) : 'No due date'}
              {overdue && task.dueAt && (
                <span className="block text-xs text-destructive">{timeAgo(task.dueAt)} overdue</span>
              )}
            </Detail>
            <Detail label="Progress">
              <span className="text-sm">
                <WorkState task={task} />
              </span>
              {task.startedAt && task.assignedAt && task.status !== 'OPEN' && (
                <span className="block text-xs text-muted-foreground">
                  First acted on {formatDistanceStrict(new Date(task.startedAt), new Date(task.assignedAt))}{' '}
                  after it was given
                </span>
              )}
            </Detail>
            <Detail label="Assigned to">
              <span className="font-medium">{task.assignee?.name ?? 'Nobody'}</span>
              {task.assignedAt && (
                <span className="block text-xs text-muted-foreground">{formatDateTime(task.assignedAt)}</span>
              )}
            </Detail>
            <Detail label="Created">
              {task.createdBy?.name ?? 'Someone'}
              <span className="block text-xs text-muted-foreground">{formatDateTime(task.createdAt)}</span>
            </Detail>
            {task.participantIds.length > 0 && (
              <Detail label="Also told">{task.participantIds.map(nameOf).join(', ')}</Detail>
            )}
            {task.entityType === 'deal' && task.entityId && (
              <Detail label="Linked to">
                <Link to={`/crm/deals/${task.entityId}`} className="text-primary hover:underline">
                  Open the deal
                </Link>
              </Detail>
            )}
          </dl>

          <RemarkTrail taskId={task.id} />
        </div>

        <UpdateComposer task={task} onPosted={setTask} />
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

// ── The trail ────────────────────────────────────────────────────────────────

/** Every update so far, oldest at the top, the way you would read a diary. */
function RemarkTrail({ taskId }: { taskId: string }) {
  const { data: remarks, isLoading } = useTaskRemarks(taskId);
  const end = useRef<HTMLDivElement>(null);

  // Land on the newest update, which is the one somebody opened this to see.
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'nearest' });
  }, [remarks?.length]);

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Updates
        {remarks && remarks.length > 0 && (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">{remarks.length}</span>
        )}
      </h3>

      {isLoading ? (
        <Skeleton className="h-16" />
      ) : !remarks || remarks.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
          No updates yet. Whoever is doing this writes here what they did and what is left.
        </p>
      ) : (
        <ol className="space-y-3">
          {remarks.map((remark) => (
            <RemarkItem key={remark.id} remark={remark} />
          ))}
        </ol>
      )}
      <div ref={end} />
    </section>
  );
}

function RemarkItem({ remark }: { remark: TaskRemark }) {
  const name = remark.author?.name ?? 'Someone';
  return (
    <li className="flex gap-2.5">
      <Avatar name={name} size={28} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium">{name}</span>
          <span className="text-xs text-muted-foreground" title={formatDateTime(remark.createdAt)}>
            {formatDateTime(remark.createdAt)} · {timeAgo(remark.createdAt)}
          </span>
        </div>
        {remark.statusTo && (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            {remark.statusFrom && (
              <>
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                  {TASK_STATUS_LABELS[remark.statusFrom]}
                </Badge>
                <ArrowRight className="h-3 w-3" />
              </>
            )}
            <Badge
              variant={remark.statusTo === 'DONE' ? 'success' : 'secondary'}
              className="px-1.5 py-0 text-[10px]"
            >
              {TASK_STATUS_LABELS[remark.statusTo]}
            </Badge>
          </div>
        )}
        {remark.body && (
          <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted/50 px-3 py-2 text-sm">{remark.body}</p>
        )}
      </div>
    </li>
  );
}

// ── The composer ─────────────────────────────────────────────────────────────

/**
 * What did you do, what is left, and where does it stand now. Marking it done
 * from here still asks first, the same as everywhere else.
 */
function UpdateComposer({ task, onPosted }: { task: Task; onPosted: (task: Task) => void }) {
  const add = useAddTaskRemark();
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => setStatus(task.status), [task.status]);

  const moving = status !== task.status;
  const canPost = body.trim().length > 0 || moving;

  const post = () =>
    add.mutate(
      { taskId: task.id, body: body.trim() || undefined, status: moving ? status : undefined },
      {
        onSuccess: (result) => {
          setBody('');
          setConfirming(false);
          onPosted(result.task);
        },
      },
    );

  const submit = () => {
    if (!canPost) return;
    if (moving && status === 'DONE') setConfirming(true);
    else post();
  };

  return (
    <div className="border-t bg-muted/30 px-5 py-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <label htmlFor="task-update-status" className="text-xs font-medium text-muted-foreground">
          Status
        </label>
        <Select
          id="task-update-status"
          value={status}
          onChange={(event) => setStatus(event.target.value as TaskStatus)}
          className="h-8 w-auto min-w-[9rem] text-sm"
        >
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {TASK_STATUS_LABELS[value]}
            </option>
          ))}
        </Select>
        {moving && (
          <span className="text-xs text-muted-foreground">
            {TASK_STATUS_LABELS[task.status]} → {TASK_STATUS_LABELS[status]}
          </span>
        )}
      </div>

      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          // Ctrl/Cmd+Enter posts; a plain Enter is a new line in a remark.
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            submit();
          }
        }}
        rows={3}
        placeholder="What did you do? What is still left, and why?"
        className="min-h-[76px] resize-y bg-background text-sm"
      />

      {add.error && (
        <div className="mt-2">
          <FormError
            message={add.error instanceof ApiRequestError ? add.error.message : 'Could not post the update.'}
          />
        </div>
      )}

      <DialogFooter className="mt-3 gap-2 sm:items-center sm:justify-between sm:gap-2">
        <span className="hidden text-xs text-muted-foreground sm:block">Ctrl + Enter to post</span>
        <Button onClick={submit} disabled={!canPost || add.isPending}>
          {moving && status === 'DONE' ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          {add.isPending ? 'Posting…' : moving && status === 'DONE' ? 'Post & mark done' : 'Post update'}
        </Button>
      </DialogFooter>

      {confirming && (
        <ConfirmDoneDialog
          task={task}
          pending={add.isPending}
          error={add.error instanceof ApiRequestError ? add.error.message : null}
          onCancel={() => setConfirming(false)}
          onConfirm={post}
        />
      )}
    </div>
  );
}

// ── Confirmation ─────────────────────────────────────────────────────────────

/**
 * "Are you sure?" before a task is marked done.
 *
 * A done task leaves the list at once, and a stray tap on a phone should not
 * be enough to make somebody's work disappear from the day it was due on.
 */
export function ConfirmDoneDialog({
  task,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  task: Task;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && !pending && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Mark this task done?</DialogTitle>
          <DialogDescription>
            &ldquo;{task.title}&rdquo; will move to Completed.
            {task.assignee && ` ${task.assignee.name} and anyone told about it will see it as done.`}
          </DialogDescription>
        </DialogHeader>

        {error && <FormError message={error} />}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            <CheckCircle2 className="h-4 w-4" />
            {pending ? 'Marking…' : 'Yes, mark done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
